import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface QueueItemEntry {
  el: HTMLElement;
  hasNewMessage: boolean;
}

interface QueueNotificationsContextValue {
  registerItem: (callerID: string, el: HTMLElement) => void;
  unregisterItem: (callerID: string) => void;
  setItemHasNewMessage: (callerID: string, hasNewMessage: boolean) => void;
  /** Caller IDs of items with a new message that are scrolled below the viewport, ordered top-to-bottom. */
  outOfViewUnreadIds: string[];
  /** Scrolls the first below-the-fold unread item into view. */
  scrollToFirstUnread: () => void;
}

const noop = () => undefined;

// Default value lets the item hook no-op when rendered outside a provider
// (e.g. Storybook, unit tests), so EmergencyCall keeps working standalone.
const QueueNotificationsContext = createContext<QueueNotificationsContextValue>(
  {
    registerItem: noop,
    unregisterItem: noop,
    setItemHasNewMessage: noop,
    outOfViewUnreadIds: [],
    scrollToFirstUnread: noop,
  },
);

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

interface QueueNotificationsProviderProps {
  /**
   * The scrollable queue container element, used as the IntersectionObserver
   * root. Must be passed as state (e.g. from a callback ref) rather than a ref
   * object so the observer is (re)created when the container mounts — the queue
   * is usually empty (no scroll container) when the provider first mounts.
   */
  scrollRoot: HTMLElement | null;
  children: React.ReactNode;
}

export const QueueNotificationsProvider = ({
  scrollRoot,
  children,
}: QueueNotificationsProviderProps) => {
  const registry = useRef(new Map<string, QueueItemEntry>());
  const elementToId = useRef(new Map<Element, string>());
  const belowFold = useRef(new Set<string>());
  const observer = useRef<IntersectionObserver | null>(null);

  const [outOfViewUnreadIds, setOutOfViewUnreadIds] = useState<string[]>([]);

  const recompute = useCallback(() => {
    const ids: string[] = [];
    belowFold.current.forEach(callerID => {
      if (registry.current.get(callerID)?.hasNewMessage) {
        ids.push(callerID);
      }
    });
    ids.sort((a, b) => {
      const topA = registry.current.get(a)?.el.getBoundingClientRect().top ?? 0;
      const topB = registry.current.get(b)?.el.getBoundingClientRect().top ?? 0;
      return topA - topB;
    });
    setOutOfViewUnreadIds(prev => (arraysEqual(prev, ids) ? prev : ids));
  }, []);

  // Create the observer once the scroll container mounts, and observe any items
  // that registered before it existed. Re-runs if the container element changes
  // (e.g. the queue goes from empty to populated).
  useEffect(() => {
    if (!scrollRoot) return undefined;

    observer.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const callerID = elementToId.current.get(entry.target);
          if (!callerID) return;
          const isBelow =
            !entry.isIntersecting &&
            !!entry.rootBounds &&
            entry.boundingClientRect.top >= entry.rootBounds.bottom;
          if (isBelow) {
            belowFold.current.add(callerID);
          } else {
            belowFold.current.delete(callerID);
          }
        });
        recompute();
      },
      { root: scrollRoot, threshold: 0 },
    );

    registry.current.forEach(entry => observer.current?.observe(entry.el));

    return () => {
      observer.current?.disconnect();
      observer.current = null;
    };
  }, [scrollRoot, recompute]);

  const registerItem = useCallback((callerID: string, el: HTMLElement) => {
    registry.current.set(callerID, {
      el,
      hasNewMessage: registry.current.get(callerID)?.hasNewMessage ?? false,
    });
    elementToId.current.set(el, callerID);
    observer.current?.observe(el);
  }, []);

  const unregisterItem = useCallback(
    (callerID: string) => {
      const entry = registry.current.get(callerID);
      if (entry) {
        observer.current?.unobserve(entry.el);
        elementToId.current.delete(entry.el);
      }
      registry.current.delete(callerID);
      belowFold.current.delete(callerID);
      recompute();
    },
    [recompute],
  );

  const setItemHasNewMessage = useCallback(
    (callerID: string, hasNewMessage: boolean) => {
      const entry = registry.current.get(callerID);
      if (!entry || entry.hasNewMessage === hasNewMessage) return;
      entry.hasNewMessage = hasNewMessage;
      recompute();
    },
    [recompute],
  );

  const scrollToFirstUnread = useCallback(() => {
    const firstId = outOfViewUnreadIds[0];
    const el = firstId ? registry.current.get(firstId)?.el : null;
    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    el?.scrollIntoView({
      block: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [outOfViewUnreadIds]);

  const value = useMemo(
    () => ({
      registerItem,
      unregisterItem,
      setItemHasNewMessage,
      outOfViewUnreadIds,
      scrollToFirstUnread,
    }),
    [
      registerItem,
      unregisterItem,
      setItemHasNewMessage,
      outOfViewUnreadIds,
      scrollToFirstUnread,
    ],
  );

  return (
    <QueueNotificationsContext.Provider value={value}>
      {children}
    </QueueNotificationsContext.Provider>
  );
};

export const useQueueNotifications = () =>
  useContext(QueueNotificationsContext);

/**
 * Registers a queue item with the notifications context so the out-of-view
 * indicator can track whether it has a new message and is below the viewport.
 * Returns a ref to attach to the item's root element. No-ops outside a provider.
 */
export const useQueueItemNotification = (
  callerID: string,
  hasNewMessage: boolean,
) => {
  const { registerItem, unregisterItem, setItemHasNewMessage } =
    useQueueNotifications();
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !callerID) return undefined;
    registerItem(callerID, el);
    return () => unregisterItem(callerID);
  }, [callerID, registerItem, unregisterItem]);

  useEffect(() => {
    if (callerID) setItemHasNewMessage(callerID, hasNewMessage);
  }, [callerID, hasNewMessage, setItemHasNewMessage]);

  return ref;
};

export default QueueNotificationsContext;
